import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import ReactDOM from 'react-dom'

import { ILabelValue } from './TokenDefinitions'
import { ZIndex } from 'util/ZIndex'

type ISuggestionProps = {
  suggestions: ILabelValue[]
  onSelect: (suggestion: ILabelValue) => void
  title: string | null
  showPrompt: boolean
  ref?: any
}

export interface ISuggestionsHandles {
  select(): void
  up(): void
  down(): void
}

const SuggestionsF: React.RefForwardingComponent<ISuggestionsHandles, ISuggestionProps> = (props, ref) => {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectSuggestion = () => {
    const suggestion = props.suggestions[selectedIndex]
    if (suggestion) {
      props.onSelect(suggestion)
    }
  }

  useEffect(() => {
    setSelectedIndex(0)
  }, [props.suggestions])

  const changeSelectedIndex = (delta: 1 | -1) => {
    const numberOfSuggestions = props.suggestions.length
    let nextIndex = selectedIndex + delta
    if (nextIndex < 0) {
      nextIndex = numberOfSuggestions - 1
    } else if (nextIndex >= numberOfSuggestions) {
      nextIndex = 0
    }
    setSelectedIndex(nextIndex)
  }

  useImperativeHandle(ref, () => ({
    select: () => {
      selectSuggestion()
    },
    up: () => {
      changeSelectedIndex(-1)
    },
    down: () => {
      changeSelectedIndex(1)
    },
  }))

  useEffect(() => {
    setTimeout(() => {
      let position = caretPosition()
      if (position) {
        position.left += window.scrollX
        position.top += window.scrollY + 20
        setPosition(position)
      }
    }, 0)
  }, [])

  const suggestions = (
    <Fragment>
      {props.suggestions.length === 0 && (
        <div className="option" style={{ opacity: 0.7 }}>
          No results
        </div>
      )}
      {props.suggestions.map((suggestion, idx) => (
        <div
          className={'option ' + (selectedIndex === idx ? 'option-selected' : '')}
          onMouseEnter={() => setSelectedIndex(idx)}
          onMouseDown={selectSuggestion}
          key={suggestion.value}>
          {suggestion.label}
        </div>
      ))}
    </Fragment>
  )

  if (position) {
    return ReactDOM.createPortal(
      <div
        className="suggestions"
        style={{ top: position.top, left: position.left, zIndex: ZIndex.CommunicationInputSuggestionsMenu }}>
        {props.title && <div className="suggestions-header">{props.title}</div>}
        <div className="options">
          {props.showPrompt ? (
            <div className="option" style={{ opacity: 0.7 }}>
              Type to search...
            </div>
          ) : (
            suggestions
          )}
        </div>
      </div>,
      document.body
    )
  }
  return null
}

export const Suggestions = forwardRef(SuggestionsF)

// From internet with love [https://stackoverflow.com/a/23526970]
function caretPosition($node?: HTMLElement, offsetx = 0, offsety = 0) {
  let nodeLeft = 0
  let nodeTop = 0
  if ($node) {
    nodeLeft = $node.offsetLeft
    nodeTop = $node.offsetTop
  }
  const pos = { left: 0, top: 0 }
  if ((document as any).selection) {
    const range = (document as any).selection.createRange()
    pos.left = range.offsetLeft + offsetx - nodeLeft
    pos.top = range.offsetTop + offsety - nodeTop
  } else if (window.getSelection) {
    const sel = window.getSelection()
    if (sel!.rangeCount === 0) return null
    const range = sel!.getRangeAt(0).cloneRange()
    try {
      range.setStart(range.startContainer, range.startOffset - 1)
    } catch (e) {
      /* tslint:disable:no-empty */
    }
    const rect = range.getBoundingClientRect()
    if (range.endOffset === 0 || range.toString() === '') {
      // first char of line
      if (range.startContainer === $node) {
        // empty div
        if (range.endOffset === 0) {
          pos.top = 0
          pos.left = 0
        } else {
          // firefox need this
          const range2 = range.cloneRange()
          range2.setStart(range2.startContainer, 0)
          const rect2 = range2.getBoundingClientRect()
          pos.left = rect2.left + offsetx - nodeLeft
          pos.top = rect2.top + rect2.height + offsety - nodeTop
        }
      } else {
        pos.top = (range.startContainer as any).offsetTop
        pos.left = (range.startContainer as any).offsetLeft
      }
    } else {
      pos.left = rect.left + rect.width + offsetx - nodeLeft
      pos.top = rect.top + offsety - nodeTop
    }
  }
  return pos
}
